/* App entry — mount React, install global providers, hydrate auth.
 *
 * Per saas-greenfield-pitfalls §11: any module-scope subscription needs
 * HMR disposal so dev hot-reloads don't accumulate listeners. */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { installVisibilityRefresh, useAuthStore } from './lib/auth';
import './index.css';

// Hydrate auth state from sessionStorage BEFORE first render so RouteGuard
// doesn't briefly send authenticated users to /login on hard refresh.
useAuthStore.getState().hydrate();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on auth errors — RouteGuard handles redirect
        const status = (error as { status?: number })?.status;
        if (status === 401 || status === 403) return false;
        return failureCount < 2;
      },
    },
  },
});

// Mount visibility-triggered token refresh; cleanup on HMR.
const removeVisibilityListener = installVisibilityRefresh();
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    removeVisibilityListener();
  });
}

const baseName = (import.meta.env.VITE_BASE_PATH as string | undefined)?.replace(/\/$/, '') || '/';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter basename={baseName}>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
